import { useEffect, useMemo, useRef } from "react";
import type { EpisodeManifest } from "@scrile/contracts";
import { t } from "../i18n";
import { currentLineIndex, normalizeTranscript } from "../lib/transcript";

/**
 * Расшифровка с подсветкой текущей реплики. Тап по строке — перемотка к ней.
 * Прокрутка следует за звуком, но уступает человеку: если он листает сам, четыре
 * секунды мы не дёргаем список обратно.
 */
export function Transcript({
  lines,
  currentTime,
  onSeek,
}: {
  lines: EpisodeManifest["transcript"];
  currentTime: number;
  onSeek: (seconds: number) => void;
}) {
  const sorted = useMemo(() => normalizeTranscript(lines), [lines]);
  const index = currentLineIndex(sorted, currentTime);
  const box = useRef<HTMLOListElement>(null);
  const userScrolledAt = useRef(0);

  useEffect(() => {
    const container = box.current;
    if (!container || index < 0) return;
    if (Date.now() - userScrolledAt.current < 4_000) return;
    const line = container.children[index];
    if (!(line instanceof HTMLElement)) return;
    const top = line.offsetTop - container.clientHeight / 2 + line.clientHeight / 2;
    container.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
  }, [index]);

  if (sorted.length === 0) return <p className="muted transcript__empty">{t("player.transcriptNone")}</p>;

  const markUserScroll = () => {
    userScrolledAt.current = Date.now();
  };

  return (
    <ol className="transcript" ref={box} onWheel={markUserScroll} onTouchMove={markUserScroll}>
      {sorted.map((line, lineIndex) => (
        <li key={`${line.startSec}-${lineIndex}`}>
          <button
            type="button"
            className={`transcript__line${lineIndex === index ? " transcript__line--current" : ""}${
              lineIndex < index ? " transcript__line--past" : ""
            }`}
            onClick={() => onSeek(line.startSec)}
            aria-current={lineIndex === index ? "true" : undefined}
          >
            {line.text}
          </button>
        </li>
      ))}
    </ol>
  );
}
